import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AdministrativeRuleAddendum,
  PendingAdministrativeRuleModel,
} from '@core/models';
import { AdministrativeRulesService } from '@core/services';
import { LayoutState } from '@core/state/shared';
import {
  DialogContentBase,
  DialogRef,
  DialogService,
} from '@progress/kendo-angular-dialog';
import { BehaviorSubject, finalize, forkJoin } from 'rxjs';
import { AdministrativeNormsViewerComponent } from '../administrative-norms-viewer/administrative-norms-viewer.component';

@Component({
  selector: 'app-administrative-rules-addendum',
  standalone: false,
  templateUrl: './administrative-rules-addendum.component.html',
  styleUrl: './administrative-rules-addendum.component.scss',
})
export class AdministrativeRulesAddendumComponent
  extends DialogContentBase
  implements OnInit
{
  @Input()
  public administrativeRule: PendingAdministrativeRuleModel;
  @Input()
  public addendums: AdministrativeRuleAddendum[];
  @Input()
  public fromGuard: boolean;
  public form: FormGroup;
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private administrativeRuleService: AdministrativeRulesService,
    private dialogRef: DialogRef,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private layoutState: LayoutState
  ) {
    super(dialogRef);
  }

  public get addendumsFormArray(): FormArray {
    return this.form.get('addendums') as FormArray;
  }

  public ngOnInit(): void {
    this.initForm();
  }

  public acceptAddendum(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading$.next(true);
    const formValues = this.form.value.addendums;
    const requests = formValues.map(
      (addendum: PendingAdministrativeRuleModel) =>
        this.administrativeRuleService.acceptAddendum(addendum)
    );
    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe((results) => {
        const allSuccessful = (results as boolean[]).every((result) => result);
        if (!allSuccessful) {
          return;
        }
        this.closeDialog('confirm');
      });
  }

  public closeDialog(action = 'cancel'): void {
    this.dialogRef.close({ text: action });
  }

  public displayCheckboxError(control: string, i: number) {
    return (
      (this.addendumsFormArray.at(i).get(control)?.dirty ||
        this.addendumsFormArray.at(i).get(control)?.touched) &&
      this.addendumsFormArray.at(i).get(control)?.errors?.['required']
    );
  }

  public showDocument(addendum: AdministrativeRuleAddendum): void {
    const dialogRef = this.dialogService.open({
      title: addendum.administrativeRuleAddendumDescription,
      content: AdministrativeNormsViewerComponent,
      actionsLayout: 'end',
      cssClass: 'confirm-dialog',
      width: this.layoutState.isMobile ? '90%' : '70%',
      maxHeight: '90%',
    });

    const administrativeNormsViewerComponent = dialogRef.content
      .instance as AdministrativeNormsViewerComponent;
    administrativeNormsViewerComponent.fromRule = false;

    administrativeNormsViewerComponent.fileUrl =
      addendum.administrativeRuleAddendumUrl;
    administrativeNormsViewerComponent.fromAddendum = true;
    administrativeNormsViewerComponent.studentName =
      this.administrativeRule.fullName;
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      addendums: this.formBuilder.array(
        this.addendums.map((addendum) => this.createAddendumForm(addendum))
      ),
    });
  }

  private createAddendumForm(addendum: AdministrativeRuleAddendum): FormGroup {
    return this.formBuilder.group({
      student_administrative_rule_id:
        this.administrativeRule.studentAdminRuleId,
      administrative_rule_addendum_id: [addendum.administrativeRuleAddendumId],
      chargeback_addendum: [false, Validators.requiredTrue],
    });
  }
}
